import { Badge, IconButton, Popover } from '@mui/material'
import { useId, useState } from 'react'
import { tv, type VariantProps } from 'tailwind-variants'
import IcBaselineNotificationsNone from '~icons/ic/baseline-notifications-none'
import { useMyInfo } from 'src/services/apollo/auth'
import { useMyUnreadNotiCounts } from 'src/services/apollo/notification'
import { track } from 'src/utils/tracker'
import { useAllNotificationTypesInTabs } from './common'
import { NotificationListContext } from './NotificationListContext'
import { useUnreadNotificationNews } from './NotificationNews/NotificationNewsButton'
import NotificationPanel from './NotificationPanel'

const useUnreadNotifications = () => {
  const { data: notis = [], refetch } = useMyUnreadNotiCounts()
  const allNotificationTypesInTabs = useAllNotificationTypesInTabs()

  // only sums unknown noti types
  const unreadCount = notis
    .filter(noti => noti.type && allNotificationTypesInTabs.includes(noti.type))
    .reduce((prev, cur) => prev + (cur.count ?? 0), 0)
  const hasNewNotifications = unreadCount > 0

  return {
    hasNewNotifications,
    unreadCount,
    notis,
    refetch,
  }
}

const buttonStyles = tv({
  slots: {
    button:
      'bg-background-secondary border border-solid border-black/50 dark:border-white/50',
    icon: 'text-primary',
  },
  variants: {
    size: {
      normal: {
        button: 'size-8',
        // `box-content` and paddings/margins are for putting the badge at the
        // right position
        icon: 'box-content size-5 p-1.5 -m-px',
      },
      large: {
        button: 'size-10',
        icon: 'box-content size-6 p-2 -m-px',
      },
    },
  },
  defaultVariants: {
    size: 'normal',
  },
})

const NotificationButton = (props: VariantProps<typeof buttonStyles>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
  const popoverId = useId()

  const {
    hasNewNotifications,
    unreadCount,
    notis,
    refetch: refetchUnread,
  } = useUnreadNotifications()

  const { hasUnread: hasUnreadNews } = useUnreadNotificationNews()

  const { data: userInfo } = useMyInfo()

  if (!userInfo) return null

  const styles = buttonStyles({ size: props.size })

  return (
    <>
      <IconButton
        title="open notification"
        aria-describedby={popoverId}
        className={styles.button({ size: props.size })}
        onClick={e => {
          setAnchorEl(e.currentTarget)
          track('notification_open')
        }}
      >
        <Badge
          overlap="circular"
          color="error"
          variant={unreadCount < 1 ? 'dot' : undefined}
          invisible={!hasNewNotifications && !hasUnreadNews}
          badgeContent={unreadCount || ''}
          showZero
        >
          <IcBaselineNotificationsNone className={styles.icon()} />
        </Badge>
      </IconButton>

      <NotificationListContext.Provider
        value={{
          open: !!anchorEl,
          unreadCounts: notis,
          closePanel: () => setAnchorEl(undefined),
        }}
      >
        <Popover
          id={popoverId}
          anchorEl={anchorEl}
          open={!!anchorEl}
          disableRestoreFocus
          onClose={() => {
            refetchUnread()
            setAnchorEl(undefined)
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          slotProps={{
            paper: {
              style: {
                borderRadius: '24px',
                border: '1px solid var(--card-border)',
              },
            },
          }}
        >
          <NotificationPanel onTabChange={() => refetchUnread()} />
        </Popover>
      </NotificationListContext.Provider>
    </>
  )
}

export { NotificationListContext } from './NotificationListContext'

export default NotificationButton
